import { IconButton, Stack, Typography } from "@mui/material";
import { motion } from "framer-motion";
// import { useTranslation } from "react-i18next";
import { FiEdit } from "react-icons/fi";

const AutobiaCreditCard = ({
  cardNetwork = "VISA",
  cardNumber = "•••• •••• •••• 4242",
  commissionRate = 0,
  refundRate = 0,
  isEnabled = true,
  onEdit = () => {},
  onToggle = () => {},
  gradientColors = ["#FA9940", "#4A60F4"], // Autobia brand colors
  index = 0,
  logo,
  showRates = true,
  showActions = true,
  customContent = null,
}) => {
  // Uppercase the first letter of the card network before using it
  const formattedCardNetwork =
    cardNetwork.charAt(0).toUpperCase() + cardNetwork.slice(1).toLowerCase();

  const normalizedCommissionRate = parseFloat(
    (commissionRate * 100).toFixed(2)
  );

  const normalizedRefundRate = parseFloat((refundRate * 100).toFixed(2));

  const t = (key, params) => {
    if (key === "financialLabels.commissionRate") return `Commission: ${params?.commissionRate}%`;
    if (key === "financialLabels.refundRate") return `Refund: ${params?.refundRate}%`;
    return key;
  };

  return (
    <Stack
      component={motion.div}
      whileHover={{ scale: 1.002 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1, ease: "easeInOut" }}
      viewport={{ once: false }}
      sx={{ 
        perspective: "750px",
        width: "100%",
        maxWidth: "320px"
      }}
    >
      <Stack
        component={motion.div}
        initial={{ rotateY: 0 }}
        whileHover={{ rotateY: 3 }}
        transition={{ duration: 0.6 }}
        sx={{
          background: `linear-gradient(135deg, ${gradientColors?.[0]} 0%, ${gradientColors?.[1]} 100%)`,
          borderRadius: "16px",
          width: "100%",
          color: "#fff",
          minHeight: "200px",
          boxShadow: `0 10px 20px ${gradientColors?.[0]}40`,
          position: "relative",
          overflow: "hidden",
          paddingY: "1.25rem",
          "&:after": {
            content: '""',
            position: "absolute",
            bottom: "-30%",
            right: "-10%",
            width: "150px",
            height: "150px",
            background: "rgba(255, 255, 255, 0.08)",
            borderRadius: "50%",
          },
          "&:before": {
            content: '""',
            position: "absolute",
            top: "-20%",
            left: "-5%",
            width: "100px",
            height: "100px",
            background: "rgba(255, 255, 255, 0.05)",
            borderRadius: "50%",
          },
        }}
        direction={"column"}
      >
        {/* Card Header */}
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          paddingX={"1.25rem"}
        >
          {/* Card Network Logo */}
          <Stack
            sx={{
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              padding: "0.25rem 0.75rem",
            }}
            justifyContent={"center"}
            alignItems={"center"}
            component={motion.div}
            whileHover={{ scale: 1.05 }}
            direction={"row"}
            gap={"0.5rem"}
          >
            {logo && (
              <img 
                src={logo} 
                alt={formattedCardNetwork}
                style={{ width: "24px", height: "16px", objectFit: "contain" }}
              />
            )}
            <Typography variant="body2" fontWeight={700}>
              {formattedCardNetwork}
            </Typography>
          </Stack>
          
          {/* Card Actions */}
          {showActions && (
            <Stack
              direction={"row"}
              gap={"0.5rem"}
              alignItems={"center"}
              zIndex={10}
            >
              <IconButton
                sx={{
                  width: "32px",
                  height: "32px",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "10px",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}
                component={motion.div}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                color="secondary"
                onClick={onEdit}
              >
                <FiEdit color={"#fff"} size={18} />
              </IconButton>
              
              {/* Custom Switch or Toggle */}
              <Stack
                sx={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  backgroundColor: isEnabled 
                    ? "rgba(76, 175, 80, 0.8)" 
                    : "rgba(158, 158, 158, 0.6)",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.2)",
                  }
                }}
                component={motion.div}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                onClick={onToggle}
              />
            </Stack>
          )}
        </Stack>
        
        {/* Card Number */}
        <Stack flexGrow={1} justifyContent={"center"} paddingX={"1.25rem"}>
          <Typography
            variant="h6"
            fontWeight={600}
            letterSpacing={2}
            fontFamily="monospace"
            marginBottom={"1rem"}
            sx={{
              textShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            {cardNumber}
          </Typography>
        </Stack>
        
        {/* Custom Content or Rates */}
        {customContent ? (
          <Stack paddingX={"1.25rem"}>
            {customContent}
          </Stack>
        ) : showRates ? (
          <Stack
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              width: "fit-content",
              color: "#fff",
              padding: "0.75rem 1rem",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
            component={motion.div}
            whileHover={{ scale: 1.02 }}
            direction={"row"}
            alignItems={"center"}
            gap={"0.75rem"}
            marginX={"auto"}
            maxWidth={"90%"}
            flexWrap="wrap"
          >
            <Typography fontSize={"0.75rem"} fontWeight={600}>
              {t ? t(`financialLabels.commissionRate`, {
                commissionRate: normalizedCommissionRate,
              }) : `Commission: ${normalizedCommissionRate}%`}
            </Typography>
            <Stack
              sx={{
                width: "4px",
                height: "4px",
                backgroundColor: "rgba(255, 255, 255, 0.6)",
                borderRadius: "50%",
              }}
            />
            <Typography fontSize={"0.75rem"} fontWeight={600}>
              {t ? t(`financialLabels.refundRate`, {
                refundRate: normalizedRefundRate,
              }) : `Refund: ${normalizedRefundRate}%`}
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default AutobiaCreditCard;